/**
 * Efficient beacon positioning utility
 * Optimized for performance with caching and reduced DOM queries
 */

import {
  startPerformanceTimer,
  endPerformanceTimer,
  recordCacheHit,
  recordCacheMiss
} from 'utils/beaconPerformance';
import {
  debugLog,
  logElementSearch,
  logPositioningAttempt
} from 'utils/beaconDebug';

// Cache for frequently accessed elements
let elementCache = new Map();
let cacheTimeout = null;

/**
 * Clear the element cache
 */
export const clearBeaconCache = () => {
  elementCache.clear();
};

/**
 * Get cached or fresh element query result
 * @param {string} selector - CSS selector
 * @param {boolean} forceRefresh - Whether to bypass cache
 * @returns {Element|null} - Found element or null
 */
const getCachedElement = (selector, forceRefresh = false) => {
  // Clear cache periodically to prevent memory leaks
  if (cacheTimeout) {
    clearTimeout(cacheTimeout);
  }
  cacheTimeout = setTimeout(() => {
    elementCache.clear();
  }, 5000); // Clear cache every 5 seconds
  
  if (!forceRefresh && elementCache.has(selector)) {
    recordCacheHit();
    const element = elementCache.get(selector);
    logElementSearch(selector, element);
    return element;
  }
  
  recordCacheMiss();
  const element = document.querySelector(selector);
  elementCache.set(selector, element);
  logElementSearch(selector, element);
  return element;
};

/**
 * Get multiple cached elements
 * @param {string[]} selectors - Array of CSS selectors
 * @returns {Element|null} - First found element or null
 */
const getFirstCachedElement = (selectors) => {
  for (const selector of selectors) {
    const element = getCachedElement(selector);
    if (element) {
      return element;
    }
  }
  return null;
};

/**
 * Position beacon on open panneau button
 * @returns {Object|null} - Position object or null if not found
 */
export const positionBeaconOnOpenPanneau = () => {
  const startTime = startPerformanceTimer();
  
  try {
    debugLog('Attempting to position beacon on open panneau button');
    
    // Try multiple selectors for the open panneau button - optimized order
    const buttonSelectors = [
      '.openPaneau button',  // Most specific first
      '.openPaneau',         // Then general class
      '#toggle-button-container button',
      '[class*="openPaneau"]',
      '#toggle-button-container .openPaneau'
    ];
    
    const openPanneauButton = getFirstCachedElement(buttonSelectors);
    
    if (openPanneauButton) {
      const rect = openPanneauButton.getBoundingClientRect();
      // Position beacon on the button, centered
      const top = rect.top + rect.height / 2;
      const left = rect.left + rect.width / 2;
      
      const position = { top: `${top}px`, left: `${left}px` };
      logPositioningAttempt('openPanneauButton', position, true);
      endPerformanceTimer(startTime, true);
      debugLog('Successfully positioned beacon on open panneau button', position);
      return position;
    }
    
    // If still not found, try to find it by text content (limited search)
    debugLog('Open panneau button not found by selectors, trying text search');
    const allButtons = document.querySelectorAll('button');
    // Limit search to avoid performance issues
    const maxButtonsToCheck = Math.min(30, allButtons.length);
    for (let i = 0; i < maxButtonsToCheck; i++) {
      const button = allButtons[i];
      const text = button.textContent?.trim();
      if (text && (text.includes('Ouvrir le panneau') || text.includes('Réduire le panneau'))) {
        const rect = button.getBoundingClientRect();
        const top = rect.top + rect.height / 2;
        const left = rect.left + rect.width / 2;
        const position = { top: `${top}px`, left: `${left}px` };
        logPositioningAttempt('openPanneauButtonByText', position, true);
        endPerformanceTimer(startTime, true);
        debugLog('Successfully positioned beacon on open panneau button by text', position);
        return position;
      }
    }
    
    logPositioningAttempt('openPanneauButton', null, false);
    endPerformanceTimer(startTime, false);
    debugLog('Failed to position beacon on open panneau button');
    return null;
  } catch (error) {
    console.error('Error positioning beacon on open panneau:', error);
    logPositioningAttempt('openPanneauButton', null, false);
    endPerformanceTimer(startTime, false);
    debugLog('Error positioning beacon on open panneau button', error);
    return null;
  }
};

/**
 * Position beacon on import data navigation item
 * @returns {Object|null} - Position object or null if not found
 */
export const positionBeaconOnImportData = () => {
  const startTime = startPerformanceTimer();
  
  try {
    debugLog('Attempting to position beacon on import data navigation item');
    
    // Optimized selector order for better performance
    const navItemSelectors = [
      '[data-tour="import-data"]',     // Most specific first
      '.nav-item-import-data',         // Then class-based
      'a[href="/talent/dataimport"]',  // Then exact href
      '.sidebar a[href*="dataimport"]',
      'a[href*="dataimport"]'
    ];
    
    const navItem = getFirstCachedElement(navItemSelectors);
    
    if (navItem) {
      const rect = navItem.getBoundingClientRect();
      
      // Position beacon on the right side of the nav item, centered vertically
      const top = rect.top + rect.height / 2;
      // Adjust position based on sidebar state (expanded or collapsed)
      const sidebar = getCachedElement('.sidebar-container') || 
                     getCachedElement('[class*="SidebarContainer"]') || 
                     getCachedElement('.sidebar');
      const isSidebarCollapsed = sidebar && (sidebar.offsetWidth < 100 || sidebar.classList.contains('collapsed'));
      const left = isSidebarCollapsed ? rect.right - 20 : rect.right - 20;
      
      const position = { top: `${top}px`, left: `${left}px` };
      logPositioningAttempt('importDataNavItem', position, true);
      endPerformanceTimer(startTime, true);
      debugLog('Successfully positioned beacon on import data navigation item', position);
      return position;
    }
    
    // If we still haven't found it, try a more general approach (limited search)
    debugLog('Import data nav item not found by selectors, trying general search');
    const allLinks = document.querySelectorAll('a');
    // Limit search to avoid performance issues
    const maxItemsToCheck = Math.min(20, allLinks.length);
    for (let i = 0; i < maxItemsToCheck; i++) {
      const item = allLinks[i];
      const text = item.textContent?.trim().toLowerCase();
      if (text && (text.includes('import data') || text.includes('data import'))) {
        const rect = item.getBoundingClientRect();
        const top = rect.top + rect.height / 2;
        const sidebar = getCachedElement('.sidebar-container') || 
                       getCachedElement('[class*="SidebarContainer"]') || 
                       getCachedElement('.sidebar');
        const isSidebarCollapsed = sidebar && (sidebar.offsetWidth < 100 || sidebar.classList.contains('collapsed'));
        const left = isSidebarCollapsed ? rect.right - 20 : rect.right - 20;
        const position = { top: `${top}px`, left: `${left}px` };
        logPositioningAttempt('importDataNavItemByText', position, true);
        endPerformanceTimer(startTime, true);
        debugLog('Successfully positioned beacon on import data navigation item by text', position);
        return position;
      }
    }
    
    logPositioningAttempt('importDataNavItem', null, false);
    endPerformanceTimer(startTime, false);
    debugLog('Failed to position beacon on import data navigation item');
    return null;
  } catch (error) {
    console.error('Error positioning beacon on import data:', error);
    logPositioningAttempt('importDataNavItem', null, false);
    endPerformanceTimer(startTime, false);
    debugLog('Error positioning beacon on import data navigation item', error);
    return null;
  }
};

/**
 * Get fallback position based on typical layout
 * @param {boolean} isDataImportPage - Whether we're on the data import page
 * @returns {Object} - Fallback position
 */
export const getFallbackPosition = (isDataImportPage) => {
  debugLog('Using fallback position', { isDataImportPage });
  
  if (isDataImportPage) {
    // Fallback for open panneau button - center of screen
    return { top: '50%', left: '50%' };
  } else {
    // Fallback for import data nav item - typical sidebar position
    const sidebar = document.querySelector('.sidebar-container') || 
                   document.querySelector('[class*="SidebarContainer"]') || 
                   document.querySelector('.sidebar');
    const isSidebarCollapsed = sidebar && (sidebar.offsetWidth < 100 || sidebar.classList.contains('collapsed'));
    const leftPosition = isSidebarCollapsed ? '60px' : '240px';
    return { top: '300px', left: leftPosition };
  }
};

export default {
  positionBeaconOnOpenPanneau,
  positionBeaconOnImportData,
  getFallbackPosition,
  clearBeaconCache
};